import type { Dict } from "../types";

export const common: Dict = {
  en: {
    "LiveSync.interrupted": "Sync interrupted. This screen may be out of date.",
    "RemoteChange.reload": "Reload",
    "RemoteChange.close": "Close",
    "RemoteChange.deleted": "This item was just deleted on another device.",
    "RemoteChange.confirmTitle": "Discard your changes?",
    "RemoteChange.confirmDescription": "Reloading replaces anything you have not saved yet.",
    "RemoteChange.confirmReload": "Reload anyway",
    // Common
    "common.cancel": "Cancel",
    "common.seeMore": "See more",
    "common.seeLess": "See less",
    "common.close": "Close",
    "common.save": "Save",
    "common.saving": "Saving...",
    "common.unsavedChangesTitle": "Unsaved changes",
    "common.unsavedChangesDescription": "You have unsaved changes. Are you sure you want to leave?",
    "common.leave": "Leave",
    "common.edit": "Edit",
    "common.delete": "Delete",
    "common.error": "Error",
    "common.success": "Success",
    "common.loading": "Loading...",
    "common.actions": "Actions",
    "common.errorDeleting": "Error deleting",
    "common.unexpectedError": "An unexpected error occurred",
    "common.all": "All",
    "common.optional": "Optional",
    "common.none": "None",
    "common.unknown": "Unknown",
    "common.ok": "OK",
    "common.retry": "Try again",
    "common.loadError": "Loading failed. Please try again.",
    "common.scanIncomplete": "Incomplete scan. Please scan again.",

    // Bilingual FR/EN field wrapper (BilingualTextField / BilingualTextareaField)
    "bilingual.addEnglish": "+ Add English version (optional)",
    "bilingual.hideEnglish": "Hide the English version",
    "bilingual.englishLabel": "English",
    "bilingual.englishPlaceholder": "Leave empty to use the French version",

    // Generic error messages used by errorToast wrapper and Supabase error mapper
    "errors.generic": "An error occurred",
    "errors.session.expired": "Your session has expired. Please sign in again.",
    "errors.session.reconnect": "Sign in again",
    "errors.supabase.uniqueViolation": "This value already exists",
    "errors.supabase.foreignKey": "Invalid reference",
    "errors.supabase.notNull": "Required field missing",
    "errors.supabase.checkViolation": "Invalid value",
    "errors.supabase.insufficientPrivilege": "Action not allowed",
    "errors.supabase.rlsViolation": "Action not allowed",
    "errors.supabase.networkError": "Connection issue, please retry",
    "errors.supabase.invalidCredentials": "Invalid credentials",
    "errors.supabase.userBanned":
      "This account has been deactivated by the salon. Contact the salon owner to restore access.",
    "errors.supabase.emailNotConfirmed": "Email not confirmed. Please check your inbox",
    "errors.supabase.userExists": "An account already exists with this email",
    "errors.supabase.weakPassword": "Password must be at least 10 characters",
    "errors.supabase.tooManyRequests": "Too many attempts, please try again shortly",
    "errors.supabase.insufficientStock": "Insufficient stock",
    "errors.supabase.insufficientStockAt": 'Not enough stock for "{product}" at {location}: {available} left.',
    "errors.supabase.insufficientStockNoLocation": 'Not enough stock for "{product}": {available} left.',
    "errors.supabase.invoiceAlreadyPaid": "Invoice already paid",
    "errors.supabase.salonMismatch": "This entry does not match the location of its invoice or balance. Reload the page.",
    "errors.supabase.priceAboveMax": "Price exceeds the maximum allowed for this service",
    "errors.supabase.priceBelowMin": "Price is below the minimum allowed for this service",
    "errors.supabase.effectivePriceBelowMin": "Discount drops the price below the service minimum",
    "errors.supabase.employeePriceBelowMin": "Employee price is below the service minimum",
    "errors.supabase.employeePriceAboveMax": "Employee price exceeds the service maximum",
    "errors.supabase.personBookedElsewhere": "This person already has an appointment at another location at this time.",
    // Lot A du 2026-09-27 : les deux refus serveur sur une facture encaissée.
    // Le serveur rend un JETON MACHINE en message et la phrase humaine dans son
    // `HINT` ; le front n'affiche pas le `HINT`, donc la phrase vit ici.
    "errors.supabase.invoiceHasSettledPayments":
      "This invoice has taken money in, so it can no longer be cancelled or deleted. To correct or refund it, go to the Invoices tab and use the credit note button.",
    "errors.supabase.invoiceCorrectionWindowClosed":
      "This invoice is settled and can no longer be corrected here. To correct it, go to the Invoices tab and use the credit note button.",
    "errors.supabase.invoiceHasActivePaymentLink": "A payment link is active for this invoice. Cancel the link before editing it.",
    "errors.supabase.appointmentInvoiceHasActivePaymentLink": "Cancel the payment link on this appointment's invoice first, then try again.",
    "errors.supabase.invoiceVoidedTakesNoPayment":
      "This invoice is cancelled or deleted, so it can no longer take any money in. To refund what it already took in, go to the Invoices tab and use the credit note button.",
    "errors.supabase.invoiceDraftCarriesPayments":
      "This draft carries payments: deleting it would erase that money from the books and the cash drawer. Open it, remove its payments first, then delete it.",
    "errors.supabase.invoiceVoidedRefundNotRemovable":
      "This invoice is cancelled or deleted, so a refund can no longer be taken off it: the money it offset would stay in the cash drawer with no invoice to justify it. If it is only in the trash, restore it from the Invoices tab and the removal becomes possible again. If it is cancelled, that refund is final: correct the drawer from the Cash Register screen, with Add Cash, if the money never left it.",
    // The freeze (2026-09-29): the net taken in on a cancelled or deleted
    // invoice no longer moves IN EITHER DIRECTION. This is the other direction,
    // and its way out is the opposite one: Withdraw Cash, not Add Cash.
    "errors.supabase.invoiceVoidedPaymentNotRemovable":
      "This invoice is cancelled or deleted, so a payment can no longer be taken off it: the refund it justified would leave the cash drawer with no payment to offset it. If it is only in the trash, restore it from the Invoices tab and the removal becomes possible again. If it is cancelled, that payment is final: correct the drawer from the Cash Register screen, with Withdraw Cash, if the money never came in.",
    // The drawer-orphan gate (2026-09-30): a batch can leave the net taken in
    // untouched and still move the cash drawer. Two tokens again, because the
    // way out is the mirror of the drawer movement the batch carries away.
    "errors.supabase.invoiceVoidedCashInNotRemovable":
      "This invoice is cancelled or deleted: these lines do not change its net taken in, but their effect on the cash drawer does not cancel out. Taking them off would lower the drawer without changing the books, and nothing would justify it any more. If it is only in the trash, restore it from the Invoices tab and the removal becomes possible again. If it is cancelled, these lines are final: correct the drawer from the Cash Register screen, with Withdraw Cash, if the money never came in.",
    "errors.supabase.invoiceVoidedCashOutNotRemovable":
      "This invoice is cancelled or deleted: these lines do not change its net taken in, but their effect on the cash drawer does not cancel out. Taking them off would raise the drawer without changing the books, and nothing would justify it any more. If it is only in the trash, restore it from the Invoices tab and the removal becomes possible again. If it is cancelled, these lines are final: correct the drawer from the Cash Register screen, with Add Cash, if the money never left it.",

    // Salon toast messages (errorToast / successToast wrapper)
    "Salon.toast.deleteFailed": "Could not delete",
    "Salon.toast.loadGiftCardsFailed": "Could not load gift cards",
    "Salon.toast.saveHairdresserFailed": "Could not save employee",

    // Day names (short)

    // Common actions
    "common.back": "Back",
    "common.next": "Next",
    "common.previous": "Previous",
    "common.previousMonth": "Previous month",
    "common.nextMonth": "Next month",
    "NotFound.title": "Page not found",
    "NotFound.description": "The page you are looking for does not exist or has moved.",
    "NotFound.backHome": "Back to home",
    "common.continue": "Continue",
    "common.processing": "Processing...",
    "common.creating": "Creating...",
    "common.confirmBooking": "Confirm Booking",
    "common.rescheduleAppointment": "Reschedule Appointment",
    "common.cancelAppointment": "Cancel Appointment",
    "Phone.noSmsWarning": "This number can't receive text messages. You won't get SMS reminders.",

    // SuccessScreen
    "success.title": "Booking Request Sent",
    "success.subtitle": "A confirmation will be sent to",
    "success.subtitle2": "shortly.",
    "success.changeTitle": "Need to make changes?",
    "success.changeDesc": "View your bookings to manage your appointment.",
    "success.viewBookings": "View My Bookings",
    "success.reviewPrompt":
      "Your booking went through smoothly? If you have 30 seconds, leave us a Google review, it would help us enormously become the leading appointment software. Thank you from the whole team!",
    "success.reviewButton": "Leave a review",
    "success.bookAnother": "Book Another Appointment",

    // Form.* : chaînes partagées par tous les champs de saisie, quel que soit
    // l'écran (revue du 2026-07-30, points 2 et 4).
    "Form.showPassword": "Show password",
    "Form.hidePassword": "Hide password",
    "Form.errors.emailRequired": "Please enter your email.",
    "Form.errors.emailInvalid": "This email address looks incomplete.",
    "Form.hints.emailTypo": "Did you mean",
    // Pas d'espace avant « ? » en anglais : c'est une convention française, pas
    // anglaise. Séparée d'emailTypo pour que chaque bloc de langue porte la
    // bonne ponctuation.
    "Form.hints.emailTypoSuffix": "?",
    "Form.errors.passwordRequired": "Please enter your password.",
    "common.confirm": "Confirm",
    // Ponctuation partagée. Elle vit dans le dictionnaire parce qu'elle DIFFÈRE
    // par langue : le français met une espace avant le deux points, l'anglais
    // non, et l'intervalle se dit « à » pour une heure mais « au » pour une
    // date. Un tiret comme ponctuation est proscrit dans tout texte destiné à
    // un humain, d'où ces trois clés plutôt qu'un « — » écrit sur place.
    "common.colonSep": ": ",
    "common.timeRangeSep": " to ",
    "common.dateRangeSep": " to ",
    "common.change": "Change",
    "common.reset": "Reset",
    "common.total": "Total",

    // AssociateCustomers
    "common.restore": "Restore",

    // Emergency contact relationship (EmergencyRelationField)
    "EmergencyRelation.placeholder": "Select",
    "EmergencyRelation.none": "None",
    "EmergencyRelation.spouse": "Spouse",
    "EmergencyRelation.mother": "Mother",
    "EmergencyRelation.father": "Father",
    "EmergencyRelation.child": "Child",
    "EmergencyRelation.sibling": "Sibling",
    "EmergencyRelation.friend": "Friend",
    "EmergencyRelation.other": "Other",
    "EmergencyRelation.otherPlaceholder": "Specify the relationship",
    "Personnel.emergencyContact.loadFailed": "The emergency contact could not be loaded. It will not be changed on save.",

    // DataImport : fenêtre d'import commune (clients, inventaire, cartes-cadeaux)
    "DataImport.title.customers": "Import clients",
    "DataImport.title.inventory": "Import inventory",
    "DataImport.title.giftCards": "Import gift cards",
    "DataImport.step.template": "Template",
    "DataImport.step.file": "File",
    "DataImport.step.preview": "Preview",
    "DataImport.step.result": "Result",
    "DataImport.downloadTemplate": "Download the Excel template",
    "DataImport.requiredLegend": "* Required columns: {columns}",
    "DataImport.columnsIntro": "Columns in the template",
    "DataImport.dropHint": "Drop your Excel or CSV file here, or click to choose it",
    "DataImport.reading": "Reading the file…",
    "DataImport.analyzing": "Checking against your data…",
    "DataImport.fileSummary": "{file} · {count} rows read",
    "DataImport.examplesSkipped": "{count} example rows ignored",
    "DataImport.count.created": "new",
    "DataImport.count.completed": "completed (empty fields filled)",
    "DataImport.count.existing": "already present",
    "DataImport.count.rejected": "rows rejected",
    "DataImport.suppliersCategoriesCreated": "{suppliers} suppliers and {categories} categories will be created",
    "DataImport.customersCreated": "{count} client profiles will be created",
    "DataImport.neverOverwrite": "Existing data is never overwritten: only empty fields are filled in.",
    "DataImport.rowLabel": "Row {row}",
    "DataImport.confirm": "Import {count} items",
    "DataImport.importing": "Importing…",
    "DataImport.back": "Back",
    "DataImport.close": "Close",
    "DataImport.startOver": "Import another file",
    "DataImport.downloadRejected": "Download rejected rows",
    "DataImport.reasonColumn": "Reason",
    "DataImport.downloadRejectedFailed": "The rejected rows file could not be created. Try again in a moment.",
    "DataImport.done": "Import complete",
    "DataImport.partial": "Import incomplete: {count} rows could not be processed. Import the same file again, nothing will be duplicated.",
    "DataImport.leaveTitle": "Stop the import?",
    "DataImport.leaveBody": "Rows already sent are saved. You can import the same file again later.",
    "DataImport.leaveConfirm": "Stop",
    "DataImport.leaveCancel": "Continue the import",
    "DataImport.journal.title": "Previous imports",
    "DataImport.journal.empty": "No previous import.",
    "DataImport.journal.line": "{created} created · {completed} completed · {existing} already present · {rejected} rejected",
    "DataImport.journal.interrupted": "Interrupted",
    "DataImport.journal.running": "In progress",
    "DataImport.fatal.empty": "This file contains no data rows.",
    "DataImport.fatal.missingColumns": "Required column missing: {columns}. Use the template or rename the column.",
    "DataImport.fatal.tooManyRows": "This file has {count} rows; the limit is {max}. Split it into several files.",
    "DataImport.fatal.unsupported": "Unsupported file. Use an Excel (.xlsx) or CSV file.",
    "DataImport.fatal.server": "The check could not be completed. Try again in a moment.",
    "DataImport.fatal.forbidden": "You are not allowed to import this data.",
    "DataImport.fatal.rateLimited": "You have reached the import limit for this type. You can try again at {time}.",
    "DataImport.fatal.importClosed": "This import can no longer receive rows. Start the import again.",
    "DataImport.journal.flowcutTeam": "Flowcut team",
    "DataImport.button": "Import",
    "DataImport.emptyCallout": "Coming from another software?",
    "DataImport.issue.required": "{column} is required.",
    "DataImport.issue.tooLong": "{column} is too long ({max} characters max).",
    "DataImport.issue.invalidEmail": "Invalid email: \"{value}\".",
    "DataImport.issue.phoneIgnored": "Unreadable phone number, ignored.",
    "DataImport.issue.invalidNumber": "{column}: \"{value}\" is not a valid number.",
    "DataImport.issue.mustBePositive": "{column} must be greater than 0.",
    "DataImport.issue.invalidDate": "{column}: \"{value}\" is not a valid date (YYYY-MM-DD).",
    "DataImport.issue.invalidBirthday": "{column}: \"{value}\" is not a valid birthday (day and month).",
    "DataImport.issue.invalidTime": "{column}: \"{value}\" is not a valid time (HH:MM).",
    "DataImport.issue.invalidChoice": "{column}: \"{value}\" is not in the list.",
    "DataImport.issue.negativeStockZeroed": "Negative stock ({value}) set to 0.",
    "DataImport.issue.issueDateFuture": "The issue date cannot be in the future.",
    "DataImport.issue.expirationBeforeIssue": "The expiration date is before the issue date.",
    "DataImport.issue.duplicateInFile": "Same item as row {firstRow} of the file.",
    "DataImport.issue.categoryOtherSupplier": "This category belongs to the supplier {supplier}.",
    "DataImport.issue.invalidRow": "Unreadable row.",
    "DataImport.issue.notProcessed": "Not processed (connection problem).",
    "DataImport.issue.skuTakenByOtherProduct": "SKU {sku} is already used by another product.",
    "DataImport.issue.serverError": "Refused by the server.",

    // Mise à jour de l'app (src/lib/appUpdate)
    "appUpdate.urgentMessage": "An important Flowcut update is available.",
    "appUpdate.reload": "Refresh",
    "appUpdate.nativeMessage": "A Flowcut update is available.",
    "appUpdate.openStore": "Update",
  },
  fr: {
    "LiveSync.interrupted": "Synchronisation interrompue. Cet écran peut ne pas être à jour.",
    "RemoteChange.reload": "Recharger",
    "RemoteChange.close": "Fermer",
    "RemoteChange.deleted": "Cet élément vient d'être supprimé sur un autre appareil.",
    "RemoteChange.confirmTitle": "Abandonner vos modifications ?",
    "RemoteChange.confirmDescription": "Recharger remplace ce que vous n'avez pas encore enregistré.",
    "RemoteChange.confirmReload": "Recharger quand même",
    // Common
    "common.cancel": "Annuler",
    "common.seeMore": "Voir plus",
    "common.seeLess": "Voir moins",
    "common.close": "Fermer",
    "common.save": "Enregistrer",
    "common.saving": "Enregistrement...",
    "common.unsavedChangesTitle": "Modifications non enregistrées",
    "common.unsavedChangesDescription": "Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?",
    "common.leave": "Quitter",
    "common.edit": "Modifier",
    "common.delete": "Supprimer",
    "common.error": "Erreur",
    "common.success": "Succès",
    "common.loading": "Chargement...",
    "common.actions": "Actions",
    "common.errorDeleting": "Erreur lors de la suppression",
    "common.unexpectedError": "Une erreur inattendue s'est produite",
    "common.all": "Toutes",
    "common.optional": "Optionnel",
    "common.none": "Aucune",
    "common.unknown": "Inconnu",
    "common.ok": "OK",
    "common.retry": "Réessayer",
    "common.loadError": "Le chargement a échoué. Veuillez réessayer.",
    "common.scanIncomplete": "Lecture incomplète. Scannez de nouveau.",

    // Bilingual FR/EN field wrapper (BilingualTextField / BilingualTextareaField)
    "bilingual.addEnglish": "+ Ajouter la version anglaise (optionnel)",
    "bilingual.hideEnglish": "Masquer la version anglaise",
    "bilingual.englishLabel": "Anglais",
    "bilingual.englishPlaceholder": "Laisser vide pour utiliser la version française",

    // Generic error messages used by errorToast wrapper and Supabase error mapper
    "errors.generic": "Une erreur est survenue",
    "errors.session.expired": "Votre session a expiré. Veuillez vous reconnecter.",
    "errors.session.reconnect": "Se reconnecter",
    "errors.supabase.uniqueViolation": "Cette valeur existe déjà",
    "errors.supabase.foreignKey": "Référence invalide",
    "errors.supabase.notNull": "Champ requis manquant",
    "errors.supabase.checkViolation": "Valeur invalide",
    "errors.supabase.insufficientPrivilege": "Action non autorisée",
    "errors.supabase.rlsViolation": "Action non autorisée",
    "errors.supabase.networkError": "Problème de connexion, réessayez",
    "errors.supabase.invalidCredentials": "Identifiants invalides",
    "errors.supabase.userBanned":
      "Ce compte a été désactivé par le salon. Contactez le propriétaire du salon pour rétablir l'accès.",
    "errors.supabase.emailNotConfirmed": "Email non confirmé. Vérifiez votre boîte courriel",
    "errors.supabase.userExists": "Un compte existe déjà avec cet email",
    "errors.supabase.weakPassword": "Le mot de passe doit contenir au moins 10 caractères",
    "errors.supabase.tooManyRequests": "Trop de tentatives, réessayez dans quelques instants",
    "errors.supabase.insufficientStock": "Stock insuffisant",
    "errors.supabase.insufficientStockAt": "Stock insuffisant pour « {product} » à {location} : il en reste {available}.",
    "errors.supabase.insufficientStockNoLocation": "Stock insuffisant pour « {product} » : il en reste {available}.",
    "errors.supabase.invoiceAlreadyPaid": "Facture déjà payée",
    "errors.supabase.salonMismatch": "Cette écriture ne correspond pas à l'emplacement de sa facture ou de sa dette. Rechargez la page.",
    "errors.supabase.priceAboveMax": "Le prix dépasse le maximum autorisé pour ce service",
    "errors.supabase.priceBelowMin": "Le prix est inférieur au minimum autorisé pour ce service",
    "errors.supabase.effectivePriceBelowMin": "Le rabais fait passer le prix sous le minimum du service",
    "errors.supabase.employeePriceBelowMin": "Le prix de l'employé est inférieur au minimum du service",
    "errors.supabase.employeePriceAboveMax": "Le prix de l'employé dépasse le maximum du service",
    "errors.supabase.personBookedElsewhere": "Cette personne a déjà un rendez-vous dans une autre succursale à cette heure.",
    // Lot A du 2026-09-27 : les deux refus serveur sur une facture encaissée.
    //
    // ⛔ « le bouton d'avoir », JAMAIS « note de crédit » (arbitrage R17), et
    //    l'onglet Factures est nommé parce que le bouton n'existe pas dans la
    //    facture ouverte.
    // ⛔ Le second ne répète PAS le `HINT` du serveur, qui dit « sa journée
    //    comptable est fermée » : ce refus couvre CINQ motifs, dont un avoir
    //    déjà émis et une paie finalisée. Nommer la journée serait faux quatre
    //    fois sur cinq.
    "errors.supabase.invoiceHasSettledPayments":
      "Cette facture a encaissé de l'argent : elle ne peut plus être annulée ni supprimée. Pour la corriger ou la rembourser, allez dans l'onglet Factures et utilisez le bouton d'avoir.",
    "errors.supabase.invoiceCorrectionWindowClosed":
      "Cette facture est encaissée et ne peut plus être corrigée ici. Pour la corriger, allez dans l'onglet Factures et utilisez le bouton d'avoir.",
    "errors.supabase.invoiceHasActivePaymentLink": "Un lien de paiement est actif sur cette facture. Annulez le lien avant de la modifier.",
    "errors.supabase.appointmentInvoiceHasActivePaymentLink": "Annulez d'abord le lien de paiement de la facture de ce rendez-vous, puis réessayez.",
    // La porte symétrique (2026-09-29) : l'argent qui ENTRE sur une facture
    // annulée est refusé, celui qui en SORT reste possible. La phrase nomme donc
    // le remboursement comme la seule suite, sans promettre une correction.
    // ⛔ « annulée OU SUPPRIMÉE », corrigé le 2026-09-29 : le garde serveur
    //    couvre aussi `deleted_at IS NOT NULL` sur une facture qui n'est PAS
    //    annulée, et la phrase mentait dans ce cas. Le couple est celui que
    //    `invoiceHasSettledPayments` emploie déjà pour les deux mêmes états.
    //    ⛔ Cette valeur FR est identique MOT POUR MOT au `HINT` levé par
    //    `public.forbid_money_in_on_voided_invoice`, et le cas de
    //    `src/lib/__tests__/toast.invoiceRefusals.test.ts` la recopie : les trois
    //    se changent du même geste, plus V1 de
    //    `supabase/tests/forbid_money_in_on_voided_invoice_test.sql`, qui compare
    //    l'indice par ÉGALITÉ EXACTE.
    "errors.supabase.invoiceVoidedTakesNoPayment":
      "Cette facture est annulée ou supprimée : elle ne peut plus rien encaisser. Pour rembourser ce qu'elle a déjà encaissé, allez dans l'onglet Factures et utilisez le bouton d'avoir.",
    "errors.supabase.invoiceDraftCarriesPayments":
      "Ce brouillon porte des paiements : le supprimer effacerait cet argent des livres et de la caisse. Ouvrez-le, retirez d'abord ses paiements, puis supprimez-le.",
    // La troisième porte du même lot (2026-09-29, Ruling R83). Le serveur refuse
    // qu'une suppression de lignes de paiement fasse MONTER l'encaissement net
    // d'une facture annulée ou mise à la corbeille.
    //
    // ⛔ CETTE VALEUR N'EST PAS IDENTIQUE MOT POUR MOT AU `HINT` du serveur, et
    //    c'est voulu, contrairement à la clef ci dessus : le `HINT` de
    //    `public.forbid_money_out_removal_on_voided_invoice` porte le MONTANT du
    //    remboursement, calculé à l'exécution, et une valeur i18n ne peut pas le
    //    porter. Même parti que
    //    `errors.supabase.invoiceHasSettledPayments`, dont le `HINT` chiffre
    //    aussi et dont la phrase ici ne chiffre pas. ⛔ Ne recopiez donc PAS ici
    //    la consigne « identique mot pour mot » de la clef voisine.
    //
    // « restaurez-la » garde son trait d'union : c'est celui, obligatoire, de
    // l'impératif suivi d'un pronom, jamais un tiret de ponctuation.
    //
    // ⛔ LA PHRASE DONNE UNE SORTIE POUR CHACUN DES DEUX ÉTATS (Ruling R17,
    //    corrigé le 2026-09-29 : la version précédente n'en donnait aucune pour
    //    une facture annulée et promettait trop pour la corbeille). Les deux
    //    sorties sont MESURÉES, pas supposées :
    //      * corbeille SANS annulation : la restauration suffit, le retrait
    //        aboutit ensuite ;
    //      * ANNULÉE : la restauration ne suffit pas, le retrait reste refusé.
    //        La sortie est donc le tiroir, et l'écran « Caisse » porte bien un
    //        bouton « Ajouter du comptant » (`CashRegister.addCash`).
    // ⛔ ET ON NE NOMME PAS L'AVOIR ICI, contrairement aux trois clefs voisines :
    //    mesuré, `create_and_apply_credit_note` sur une facture DÉJÀ annulée est
    //    refusée (« Only a paid or partially paid invoice can be credited »).
    //    Proposer « le bouton d'avoir » serait une sortie FAUSSE.
    "errors.supabase.invoiceVoidedRefundNotRemovable":
      "Cette facture est annulée ou supprimée : son remboursement ne peut plus lui être retiré, car l'encaissement qu'il compensait resterait dans la caisse sans facture pour le justifier. Si elle est seulement dans la corbeille, restaurez-la depuis l'onglet Factures et le retrait redevient possible. Si elle est annulée, ce remboursement est définitif : corrigez le tiroir depuis l'écran Caisse, avec « Ajouter du comptant », si l'argent n'en est jamais sorti.",
    // ⭐ LE GEL DU NET (2026-09-29, décision de Julien) : sur une facture annulée
    //    ou mise à la corbeille, le net encaissé ne change plus DANS LES DEUX
    //    SENS. Voici l'autre sens, et sa SORTIE est l'opposée de celle du jeton
    //    voisin : ici l'argent manquerait au tiroir, donc « Retirer du comptant »
    //    (`CashRegister.withdrawCash`, rendu à côté de « Ajouter du comptant »
    //    dans `src/components/invoicing/CashRegisterDialog.tsx`).
    // ⛔ LES DEUX SORTIES SONT MESURÉES, comme celles du jeton voisin : la
    //    restauration SUFFIT pour une facture seulement à la corbeille, elle NE
    //    SUFFIT PAS pour une facture annulée. Et on ne nomme pas l'avoir, pour la
    //    même raison qu'au-dessus.
    "errors.supabase.invoiceVoidedPaymentNotRemovable":
      "Cette facture est annulée ou supprimée : son encaissement ne peut plus lui être retiré, car le remboursement qu'il justifiait sortirait de la caisse sans encaissement pour le compenser. Si elle est seulement dans la corbeille, restaurez-la depuis l'onglet Factures et le retrait redevient possible. Si elle est annulée, cet encaissement est définitif : corrigez le tiroir depuis l'écran Caisse, avec « Retirer du comptant », si l'argent n'y est jamais entré.",
    // ⭐ LA PORTE D'ORPHELIN DE TIROIR (2026-09-30, décision de Julien). Un lot
    //    peut laisser l'encaissement net d'une facture annulée EXACTEMENT où il
    //    était et déplacer quand même le tiroir, quand la facture garde d'autres
    //    lignes. Deux orphelins de 10,00 $ avaient été mesurés ainsi.
    // ⛔ LA SORTIE EST LE MIROIR DU MOUVEMENT QUE LE LOT EMPORTE, et c'est la
    //    convention du dépôt, mesurée le 2026-09-30 sur les six décors de refus
    //    existants et non supposée : le refus EMPÊCHE le geste, donc l'écart
    //    qu'il aurait créé n'existe jamais. Ce que la phrase offre, c'est de
    //    corriger le tiroir quand la ligne visée enregistre un mouvement qui n'a
    //    pas eu lieu. Un lot qui emporte un `cash_in` renvoie donc vers
    //    « Retirer du comptant », un lot qui emporte un `cash_out` vers
    //    « Ajouter du comptant ».
    // ⛔ LES DEUX SORTIES SONT MESURÉES, comme celles des deux clés voisines :
    //    la restauration SUFFIT pour une facture seulement à la corbeille, elle
    //    NE SUFFIT PAS pour une facture annulée. Et on ne nomme pas l'avoir,
    //    pour la même raison : `create_and_apply_credit_note` sur une facture
    //    DÉJÀ annulée est refusée.
    // ⚠️ Le serveur CHIFFRE le mouvement de tiroir dans son `HINT` ; ces valeurs
    //    ne le peuvent pas, un montant se calcule à l'exécution.
    "errors.supabase.invoiceVoidedCashInNotRemovable":
      "Cette facture est annulée ou supprimée : ces lignes ne changent pas son encaissement net, mais leur effet sur la caisse ne s'annule pas. Les retirer ferait baisser le tiroir sans rien changer aux livres, et plus rien ne le justifierait. Si elle est seulement dans la corbeille, restaurez-la depuis l'onglet Factures et le retrait redevient possible. Si elle est annulée, ces lignes sont définitives : corrigez le tiroir depuis l'écran Caisse, avec « Retirer du comptant », si l'argent n'y est jamais entré.",
    "errors.supabase.invoiceVoidedCashOutNotRemovable":
      "Cette facture est annulée ou supprimée : ces lignes ne changent pas son encaissement net, mais leur effet sur la caisse ne s'annule pas. Les retirer ferait monter le tiroir sans rien changer aux livres, et plus rien ne le justifierait. Si elle est seulement dans la corbeille, restaurez-la depuis l'onglet Factures et le retrait redevient possible. Si elle est annulée, ces lignes sont définitives : corrigez le tiroir depuis l'écran Caisse, avec « Ajouter du comptant », si l'argent n'en est jamais sorti.",

    // Salon toast messages (errorToast / successToast wrapper)
    "Salon.toast.deleteFailed": "Impossible de supprimer",
    "Salon.toast.loadGiftCardsFailed": "Impossible de charger les cartes-cadeaux",
    "Salon.toast.saveHairdresserFailed": "Impossible d'enregistrer l'employé",

    // Day names (short)

    // Common actions
    "common.back": "Retour",
    "common.next": "Suivant",
    "common.previous": "Précédent",
    "common.previousMonth": "Mois précédent",
    "common.nextMonth": "Mois suivant",
    "NotFound.title": "Page introuvable",
    "NotFound.description": "La page que vous cherchez n'existe pas ou a été déplacée.",
    "NotFound.backHome": "Retour à l'accueil",
    "common.continue": "Continuer",
    "common.processing": "Traitement en cours...",
    "common.creating": "Création en cours...",
    "common.confirmBooking": "Confirmer la réservation",
    "common.rescheduleAppointment": "Reporter le rendez-vous",
    "common.cancelAppointment": "Annuler le rendez-vous",
    "Phone.noSmsWarning": "Ce numéro ne peut pas recevoir de textos. Vous ne recevrez pas de rappel par SMS.",

    // SuccessScreen
    "success.title": "Demande de réservation envoyée",
    "success.subtitle": "Une confirmation sera envoyée à",
    "success.subtitle2": "sous peu.",
    "success.changeTitle": "Besoin de modifications ?",
    "success.changeDesc":
      "Consultez vos réservations pour gérer votre rendez-vous.",
    "success.viewBookings": "Voir mes réservations",
    "success.reviewPrompt":
      "Votre réservation s'est bien déroulée? Si vous avez 30 secondes, laissez-nous un avis Google, ça nous aiderait énormément à devenir la référence en logiciel de rendez-vous. Merci de la part de toute l'équipe!",
    "success.reviewButton": "Laisser un avis",
    "success.bookAnother": "Réserver un autre rendez-vous",

    // Form.* : chaînes partagées par tous les champs de saisie, quel que soit
    // l'écran (revue du 2026-07-30, points 2 et 4).
    "Form.showPassword": "Afficher le mot de passe",
    "Form.hidePassword": "Masquer le mot de passe",
    "Form.errors.emailRequired": "Veuillez entrer votre courriel.",
    // « semble incomplète » plutôt que « est invalide » : la vérification de
    // format est permissive, le message doit rester vrai quand elle se trompe.
    "Form.errors.emailInvalid": "Cette adresse courriel semble incomplète.",
    "Form.hints.emailTypo": "Vouliez-vous dire",
    "Form.hints.emailTypoSuffix": " ?",
    "Form.errors.passwordRequired": "Veuillez entrer votre mot de passe.",
    "common.confirm": "Confirmer",
    "common.colonSep": " : ",
    "common.timeRangeSep": " à ",
    "common.dateRangeSep": " au ",
    "common.change": "Changer",
    "common.reset": "Réinitialiser",
    "common.total": "Total",

    // AssociateCustomers
    "common.restore": "Restaurer",

    // Lien de parenté du contact d'urgence (EmergencyRelationField)
    "EmergencyRelation.placeholder": "Choisir",
    "EmergencyRelation.none": "Aucun",
    "EmergencyRelation.spouse": "Conjoint(e)",
    "EmergencyRelation.mother": "Mère",
    "EmergencyRelation.father": "Père",
    "EmergencyRelation.child": "Enfant",
    "EmergencyRelation.sibling": "Frère ou sœur",
    "EmergencyRelation.friend": "Ami(e)",
    "EmergencyRelation.other": "Autre",
    "EmergencyRelation.otherPlaceholder": "Précisez le lien",
    "Personnel.emergencyContact.loadFailed": "Le contact d'urgence n'a pas pu être chargé. Il ne sera pas modifié à l'enregistrement.",

    // DataImport : fenêtre d'import commune (clients, inventaire, cartes-cadeaux)
    "DataImport.title.customers": "Importer des clients",
    "DataImport.title.inventory": "Importer l'inventaire",
    "DataImport.title.giftCards": "Importer des cartes-cadeaux",
    "DataImport.step.template": "Gabarit",
    "DataImport.step.file": "Fichier",
    "DataImport.step.preview": "Aperçu",
    "DataImport.step.result": "Résultat",
    "DataImport.downloadTemplate": "Télécharger le gabarit Excel",
    "DataImport.requiredLegend": "* Colonnes obligatoires : {columns}",
    "DataImport.columnsIntro": "Colonnes du gabarit",
    "DataImport.dropHint": "Déposez votre fichier Excel ou CSV ici, ou cliquez pour le choisir",
    "DataImport.reading": "Lecture du fichier…",
    "DataImport.analyzing": "Vérification avec vos données…",
    "DataImport.fileSummary": "{file} · {count} lignes lues",
    "DataImport.examplesSkipped": "{count} lignes d'exemple ignorées",
    "DataImport.count.created": "nouveaux",
    "DataImport.count.completed": "complétés (champs vides remplis)",
    "DataImport.count.existing": "déjà présents",
    "DataImport.count.rejected": "lignes refusées",
    "DataImport.suppliersCategoriesCreated": "{suppliers} fournisseurs et {categories} catégories seront créés",
    "DataImport.customersCreated": "{count} fiches clients seront créées",
    "DataImport.neverOverwrite": "Les données existantes ne sont jamais écrasées : seuls les champs vides sont complétés.",
    "DataImport.rowLabel": "Ligne {row}",
    "DataImport.confirm": "Importer {count} éléments",
    "DataImport.importing": "Import en cours…",
    "DataImport.back": "Retour",
    "DataImport.close": "Fermer",
    "DataImport.startOver": "Importer un autre fichier",
    "DataImport.downloadRejected": "Télécharger les lignes refusées",
    "DataImport.reasonColumn": "Raison",
    "DataImport.downloadRejectedFailed": "Le fichier des lignes refusées n'a pas pu être créé. Réessayez dans un instant.",
    "DataImport.done": "Import terminé",
    "DataImport.partial": "Import incomplet : {count} lignes n'ont pas pu être traitées. Relancez le même fichier, rien ne sera dupliqué.",
    "DataImport.leaveTitle": "Arrêter l'import ?",
    "DataImport.leaveBody": "Les lignes déjà envoyées sont enregistrées. Vous pourrez relancer le même fichier plus tard.",
    "DataImport.leaveConfirm": "Arrêter",
    "DataImport.leaveCancel": "Continuer l'import",
    "DataImport.journal.title": "Imports précédents",
    "DataImport.journal.empty": "Aucun import précédent.",
    "DataImport.journal.line": "{created} créés · {completed} complétés · {existing} déjà présents · {rejected} refusés",
    "DataImport.journal.interrupted": "Interrompu",
    "DataImport.journal.running": "En cours",
    "DataImport.fatal.empty": "Ce fichier ne contient aucune ligne de données.",
    "DataImport.fatal.missingColumns": "Colonne obligatoire absente : {columns}. Utilisez le gabarit ou renommez la colonne.",
    "DataImport.fatal.tooManyRows": "Ce fichier compte {count} lignes, la limite est de {max}. Découpez-le en plusieurs fichiers.",
    "DataImport.fatal.unsupported": "Fichier non pris en charge. Utilisez un fichier Excel (.xlsx) ou CSV.",
    "DataImport.fatal.server": "La vérification n'a pas pu aboutir. Réessayez dans un instant.",
    "DataImport.fatal.forbidden": "Vous n'avez pas l'autorisation d'importer ces données.",
    "DataImport.fatal.rateLimited": "Vous avez atteint la limite d'imports pour ce type. Vous pourrez réessayer à {time}.",
    "DataImport.fatal.importClosed": "Cet import ne peut plus recevoir de lignes. Relancez l'import depuis le début.",
    "DataImport.journal.flowcutTeam": "Équipe Flowcut",
    "DataImport.button": "Importer",
    "DataImport.emptyCallout": "Vous venez d'un autre logiciel ?",
    "DataImport.issue.required": "{column} est obligatoire.",
    "DataImport.issue.tooLong": "{column} est trop long ({max} caractères au plus).",
    "DataImport.issue.invalidEmail": "Courriel invalide : « {value} ».",
    "DataImport.issue.phoneIgnored": "Numéro de téléphone illisible, ignoré.",
    "DataImport.issue.invalidNumber": "{column} : « {value} » n'est pas un nombre valide.",
    "DataImport.issue.mustBePositive": "{column} doit être plus grand que 0.",
    "DataImport.issue.invalidDate": "{column} : « {value} » n'est pas une date valide (AAAA-MM-JJ).",
    "DataImport.issue.invalidBirthday": "{column} : « {value} » n'est pas un anniversaire valide (jour et mois).",
    "DataImport.issue.invalidTime": "{column} : « {value} » n'est pas une heure valide (HH:MM).",
    "DataImport.issue.invalidChoice": "{column} : « {value} » ne fait pas partie de la liste.",
    "DataImport.issue.negativeStockZeroed": "Stock négatif ({value}) ramené à 0.",
    "DataImport.issue.issueDateFuture": "La date d'émission ne peut pas être dans le futur.",
    "DataImport.issue.expirationBeforeIssue": "La date d'expiration précède la date d'émission.",
    "DataImport.issue.duplicateInFile": "Même élément que la ligne {firstRow} du fichier.",
    "DataImport.issue.categoryOtherSupplier": "Cette catégorie appartient au fournisseur {supplier}.",
    "DataImport.issue.invalidRow": "Ligne illisible.",
    "DataImport.issue.notProcessed": "Non traitée (problème de connexion).",
    "DataImport.issue.skuTakenByOtherProduct": "Le SKU {sku} est déjà utilisé par un autre produit.",
    "DataImport.issue.serverError": "Refusée par le serveur.",

    // Mise à jour de l'app (src/lib/appUpdate)
    "appUpdate.urgentMessage": "Une mise à jour importante de Flowcut est disponible.",
    "appUpdate.reload": "Actualiser",
    "appUpdate.nativeMessage": "Une mise à jour de Flowcut est disponible.",
    "appUpdate.openStore": "Mettre à jour",
  },
};
